import { useEffect, useState } from 'react';
import { listPublishedProducts, listCustomerProductFiles, downloadProductFileUrl } from '@/services/products';
import { listCurrentUserLicenses } from '@/services/licenses';
import { listCurrentUserOrders } from '@/services/orders';
import type { CustomerProductFile } from '@/services/products';
import type { Product, License, Order } from '@/domain/types';
import { formatDate } from '@/lib/format';

export default function DashboardDownloads() {
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [productFiles, setProductFiles] = useState<CustomerProductFile[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const [p, f, l, o] = await Promise.all([
          listPublishedProducts(),
          listCustomerProductFiles(),
          listCurrentUserLicenses(),
          listCurrentUserOrders(),
        ]);
        if (mounted) { setProducts(p); setProductFiles(f); setLicenses(l); setOrders(o); }
      } catch {
        if (mounted) { setProducts([]); setProductFiles([]); setLicenses([]); }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  // Only show files for products the user has paid for
  const paidProductIds = new Set(
    orders.filter((o) => o.status === 'paid').map((o) => o.productId),
  );

  const productReleases = productFiles
    .filter((f) => paidProductIds.has(f.productId))
    .filter((r) => !selectedProduct || r.productId === selectedProduct);

  const handleDownloadPlugin = async (file: CustomerProductFile) => {
    setDownloadingId(`plugin-${file.id}`);
    try {
      const ext = file.storagePath.split('.').pop() || 'zip';
      const url = await downloadProductFileUrl(file.id, `${file.slug}-${file.version}.${ext}`);
      window.open(url, '_blank');
    } catch {
      // silent
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadLicenseKey = (file: CustomerProductFile) => {
    const lic = licenses.find((l) => l.productId === file.productId);
    if (!lic) return;
    const key = lic.licenseKey || `****-****-****-${lic.licenseKeyLast4}`;
    const content = `License Key: ${key}\nProduct: ${file.productName}\nVersion: ${file.version}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file.productName.replace(/\s+/g, '-').toLowerCase()}-license-key.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const licensedProductIds = new Set(
    licenses.map((l) => l.productId),
  );

  const filteredProducts = products.filter((p) => licensedProductIds.has(p.id));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">Downloads</h1>
        <p className="text-sm text-surface-500 mt-1">Download your purchased products and view version history.</p>
      </div>

      <div className="bg-white rounded-xl border border-surface-200 p-4">
        <label className="block text-sm font-medium text-surface-700 mb-2">Filter by Product</label>
        <select
          value={selectedProduct}
          onChange={(e) => setSelectedProduct(e.target.value)}
          className="px-3 py-2 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
        >
          <option value="">All Products</option>
          {filteredProducts.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
          {filteredProducts.length === 0 && !loading && (
            <option disabled>No products available</option>
          )}
        </select>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-surface-400 text-xl" />
          </div>
        ) : productReleases.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-surface-200">
            <i className="ri-download-cloud-line text-4xl text-surface-300 mb-3" />
            <p className="text-surface-500 font-medium">No downloads available</p>
            <p className="text-sm text-surface-400 mt-1">Purchase a product to unlock downloads.</p>
          </div>
        ) : (
          productReleases.map((rel) => (
          <div key={rel.id} className="bg-white rounded-xl border border-surface-200 p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="font-semibold text-surface-900 text-sm">{rel.productName}</h3>
                <span className="px-2 py-0.5 rounded-md bg-brand-50 text-brand-700 text-xs font-semibold">
                  v{rel.version}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium capitalize">
                  {rel.status}
                </span>
              </div>
              <p className="text-xs text-surface-500 mb-2 line-clamp-2">{rel.changelog}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-surface-400">
                <span>WP {rel.requiresWp}+</span>
                <span>PHP {rel.requiresPhp}+</span>
                <span>Tested up to {rel.testedUpTo}</span>
                <span>{formatDate(rel.createdAt)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => handleDownloadPlugin(rel)}
                disabled={downloadingId === `plugin-${rel.id}`}
                className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white text-sm font-medium transition-colors whitespace-nowrap">
                {downloadingId === `plugin-${rel.id}` ? (
                  <i className="ri-loader-4-line animate-spin mr-1" />
                ) : (
                  <i className="ri-download-line mr-1" />
                )}
                {downloadingId === `plugin-${rel.id}` ? 'Preparing…' : 'Plugin File'}
              </button>
              <button
                onClick={() => handleDownloadLicenseKey(rel)}
                className="px-4 py-2 rounded-lg border border-surface-300 hover:border-brand-400 text-surface-700 hover:text-brand-600 text-sm font-medium transition-colors whitespace-nowrap">
                <i className="ri-file-text-line mr-1" /> License Key
              </button>
              <a
                href={`/docs/${rel.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-lg border border-surface-300 hover:border-brand-400 text-surface-700 hover:text-brand-600 text-sm font-medium transition-colors whitespace-nowrap inline-flex items-center gap-1.5">
                <i className="ri-book-open-line" /> Docs
              </a>
            </div>
          </div>
          ))
        )}
      </div>
    </div>
  );
}